/**
 * Video utility functions for story components
 */

/**
 * Extract YouTube video ID from various URL formats
 */
export function getYouTubeID(url: string): string {
  // shorts/ and live/ too: a Shorts link used to embed as a blank player.
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|live\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : '';
}

/**
 * Extract TikTok video ID from various URL formats
 * Supports formats like:
 * - https://www.tiktok.com/@username/video/1234567890123456789
 * - https://vm.tiktok.com/ZMabcdefg/
 * - https://www.tiktok.com/t/ZMabcdefg/
 */
export function getTikTokID(url: string): string {
  // Match standard TikTok video URL
  const standardMatch = url.match(/\/video\/(\d+)/);
  if (standardMatch) {
    return standardMatch[1];
  }
  
  // For short URLs (vm.tiktok.com or tiktok.com/t/), we can't extract the ID directly
  // These need to be resolved server-side or we use the full URL
  // For now, return empty string to indicate we should use the full URL
  return '';
}

/**
 * Format view count (e.g., 1234 -> "1.2K")
 */
export function formatViews(views: number): string {
  if (views >= 1000000) {
    return `${(views / 1000000).toFixed(1)}M`;
  }
  if (views >= 1000) {
    return `${(views / 1000).toFixed(1)}K`;
  }
  return views.toString();
}

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 */
export function formatTimeAgo(timestamp: number | string): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  const now = Date.now();
  const diff = now - date;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  
  if (years > 0) return `${years} year${years > 1 ? 's' : ''} ago`;
  if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'Just now';
}

/**
 * Generate guest ID for anonymous commenting
 */
export function getOrCreateGuestId(): string {
  const STORAGE_KEY = 'temaku_guest_id';
  let guestId = localStorage.getItem(STORAGE_KEY);
  
  if (!guestId) {
    // Generate simple UUID-like string
    guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(STORAGE_KEY, guestId);
  }
  
  return guestId;
}

/**
 * Generate guest avatar URL using DiceBear API
 */
export function getGuestAvatar(guestId: string): string {
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${guestId}`;
}

/**
 * Generate guest name
 */
export function getGuestName(guestId: string): string {
  const num = parseInt(guestId.split('_')[1] || '0') % 1000;
  return `Guest ${num}`;
}
